// src/rms/components/AgreementReader.jsx
// ============================================================================
// A contract, laid out to be read
// ============================================================================
// One component, three places: the checkout screen where a customer reads
// before signing, the printable record afterwards, and (in the remote flow) the
// public signing page.
//
// It renders a STORED snapshot and substitutes nothing. Tokens were resolved
// once, at the moment of signing, and the text handed here is the text that was
// agreed to. If this component ever grows an argument it interpolates, the
// guarantee that the page and the PDF show the same words has gone.
//
// The `scroll` variant caps its height so a two-thousand-word agreement does
// not push the signature pad off the bottom of a counter screen. It is a
// display choice and nothing more -- the whole text is present either way, and
// nothing here decides what the customer "has" read.
// ============================================================================

import React, { useMemo } from "react";
import { toBlocks } from "../agreements/render";

export default function AgreementReader({ body, maxHeight = null, dense = false }) {
  const blocks = useMemo(() => toBlocks(body), [body]);

  const size = dense ? 12.5 : 13.5;
  const lead = dense ? 1.55 : 1.65;

  return (
    <div
      className="rms-agreement-body"
      style={
        maxHeight
          ? {
              maxHeight,
              overflowY: "auto",
              background: "var(--rms-subtle)",
              border: "1px solid var(--rms-border)",
              borderRadius: 9,
              padding: 12,
            }
          : undefined
      }
    >
      {blocks.map((block, i) => {
        if (block.type === "heading") {
          return (
            <h3
              key={i}
              style={{
                fontSize: size + 0.5,
                fontWeight: 650,
                margin: i === 0 ? "0 0 6px" : "18px 0 6px",
                // Keeps a clause number from being orphaned at the foot of a
                // printed page, away from the clause it numbers.
                breakAfter: "avoid",
                breakInside: "avoid",
              }}
            >
              {block.text}
            </h3>
          );
        }

        if (block.type === "list") {
          return (
            <ul
              key={i}
              style={{
                fontSize: size,
                lineHeight: lead,
                color: "var(--rms-muted-strong)",
                paddingLeft: 20,
                margin: "6px 0",
              }}
            >
              {block.items.map((item, j) => (
                <li key={j} style={{ marginBottom: 3 }}>
                  {item}
                </li>
              ))}
            </ul>
          );
        }

        return (
          <p
            key={i}
            style={{
              fontSize: size,
              lineHeight: lead,
              color: "var(--rms-muted-strong)",
              margin: "6px 0",
            }}
          >
            {block.text}
          </p>
        );
      })}
    </div>
  );
}
